/**
 * The daily leaderboard (TRD-09 §4's Leaderboard stop; TRD-11 serves it).
 *
 * Same-origin fetch — /api is proxied to the api service in every deployment shape —
 * so there is no URL to configure and no CORS to appease. The board is read-only and
 * cache-friendly (the server sets max-age=30); a refresh button beats polling.
 */
import { useCallback, useEffect, useState } from 'react';
import { daysBetweenDates, shiftDate } from '@prism/sim';
import { MenuParticles } from './MenuParticles.js';
import { RunPeek, type PeekTarget } from './RunPeek.js';
import { isTypingTarget } from './typingTarget.js';

interface Entry {
  rank: number;
  playerId: string;
  displayName: string;
  score: number;
  wavesCleared: number;
  coreHp: number;
}

interface BoardState {
  status: 'loading' | 'error' | 'ready';
  entries: Entry[];
  /** Own rank when outside the fetched slice, from the `around` query. */
  around: number | null;
  error?: string;
}

/** A past day that has runs on it, from `/api/leaderboard/days`. */
interface DayEntry {
  date: string;
  runs: number;
}

/**
 * How a date reads to a player: "Today", "Yesterday", then the date itself.
 *
 * Relative for the two days anyone thinks about relatively, absolute after that —
 * "6 days ago" is a subtraction the reader has to do to know which board they are on.
 */
export function dateLabel(date: string, today: string): string {
  const delta = daysBetweenDates(date, today);
  if (delta === 0) return 'Today';
  if (delta === 1) return 'Yesterday';
  return date;
}

/**
 * The previous day worth showing: the newest day BEFORE `from` that has runs, or simply
 * yesterday when the day list is unavailable.
 *
 * Stepping one calendar day at a time is the obvious implementation and the wrong one —
 * a board that only ran on weekends would make the player click through five empty days
 * to reach the next real one.
 */
export function adjacentDay(
  from: string,
  direction: -1 | 1,
  days: readonly DayEntry[],
  today: string,
): string | null {
  // Nothing known: the day list failed to load, or no run has ever been submitted. Fall
  // back to plain calendar steps rather than trapping the player on one board — a broken
  // shortcut should cost the shortcut, not the feature.
  if (days.length === 0) {
    const stepped = shiftDate(from, direction);
    return stepped <= today ? stepped : null;
  }

  const candidates = days
    .map((d) => d.date)
    .filter((d) => (direction === -1 ? d < from : d > from && d <= today));
  if (candidates.length > 0) {
    // Nearest first: the largest earlier date, or the smallest later one.
    return direction === -1
      ? candidates.reduce((a, b) => (a > b ? a : b))
      : candidates.reduce((a, b) => (a < b ? a : b));
  }
  // No recorded day that way. Backwards there is nothing left to show; forwards, today is
  // always a destination even when nobody has submitted to it yet.
  if (direction === 1 && from < today) return today;
  return null;
}

interface LeaderboardProps {
  /** Today's date. The board opens here and can walk backwards from it. */
  date: string;
  playerId: string;
  /** Editable here because the settings screen is hidden in the public build — a
   *  leaderboard where nobody can pick a name is a column of "anonymous". Applies
   *  from the NEXT submission; the server keeps the name each run was sent with. */
  displayName: string;
  onDisplayName: (name: string) => void;
  onBack: () => void;
}

export function Leaderboard({ date, playerId, displayName, onDisplayName, onBack }: LeaderboardProps) {
  const [board, setBoard] = useState<BoardState>({ status: 'loading', entries: [], around: null });
  const [peek, setPeek] = useState<PeekTarget | null>(null);
  /** Which day is on screen. Opens on today; the arrows walk it back. */
  const [viewDate, setViewDate] = useState(date);
  const [days, setDays] = useState<DayEntry[]>([]);

  // Which past days have anything on them. Fetched once — it changes at most once a day,
  // and a failure here only costs the arrows their shortcut, never the board itself.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/leaderboard/days');
        if (!res.ok) return;
        const body = (await res.json()) as { days: DayEntry[] };
        if (!cancelled && Array.isArray(body.days)) setDays(body.days);
      } catch {
        /* the board still works one calendar day at a time */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setBoard({ status: 'loading', entries: [], around: null });
    try {
      const res = await fetch(
        `/api/leaderboard/${viewDate}?limit=50&around=${encodeURIComponent(playerId)}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { entries: Entry[]; around: number | null };
      setBoard({ status: 'ready', entries: body.entries, around: body.around });
    } catch (err) {
      setBoard({
        status: 'error',
        entries: [],
        around: null,
        error: err instanceof Error ? err.message : 'unreachable',
      });
    }
  }, [viewDate, playerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const ownRank = board.entries.find((e) => e.playerId === playerId)?.rank ?? board.around;
  const previous = adjacentDay(viewDate, -1, days, date);
  const next = adjacentDay(viewDate, 1, days, date);

  // ← and → move between days, because a board you browse is a board you page through.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Same trap as the run screen's hotkeys: the name field is on this page, and the
      // arrows would page the board out from under someone editing their own name.
      if (isTypingTarget(e.target)) return;
      if (e.key === 'ArrowLeft' && previous) setViewDate(previous);
      if (e.key === 'ArrowRight' && next) setViewDate(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previous, next]);

  const runsToday = days.find((d) => d.date === viewDate)?.runs ?? board.entries.length;

  return (
    <div className="lb">
      <div className="menu__bloom" />
      <MenuParticles seed={3} />

      <div className="lb__head">
        <button type="button" className="lb__chip" onClick={onBack}>
          <span>&lsaquo; MENU</span>
        </button>

        <div className="lb__mark-title">
          <span className="lb__mark-bar" />
          <h2 className="lb__title">LEADERBOARD</h2>
        </div>
        <span className="lb__sub">
          Every archived daily. Click a run to see the board it finished on.
        </span>

        <div className="lb__mode">
          <span className="lb__mode-tag">
            <span>DAILY</span>
          </span>
          <span className="lb__mode-note">one seed per day</span>
        </div>
      </div>

      <div className="lb__rule" />

      <div className="lb__table">
        {/* LOADOUT, TIME and Δ from the design are not here, and the reason is data, not
            taste. A leaderboard row carries rank, name, score, waves and core HP —
            nothing records a run's duration, the list payload has no board on it (that
            is a per-player fetch), and there is no previous-day figure to difference
            against. CORE takes one of the freed columns because it is real and it is the
            number that says how close the run actually was. */}
        <div className="lb__head-row">
          <span>#</span>
          <span>PLAYER</span>
          <span>WAVE</span>
          <span>CORE</span>
          <span>SCORE</span>
        </div>
        <div className="lb__head-rule" />

        {board.status === 'loading' && <p className="muted lb__note">loading…</p>}

        {board.status === 'error' && (
          <p className="warn lb__note">
            Leaderboard unreachable ({board.error}). The API may not be running — your run is
            queued and will submit when it is.
          </p>
        )}

        {board.status === 'ready' && board.entries.length === 0 && (
          <p className="muted lb__note">
            {viewDate === date
              ? 'No submissions yet today. Yours could be first.'
              : 'No runs were submitted on this day.'}
          </p>
        )}

        {board.status === 'ready' && board.entries.length > 0 && (
          <div className="lb__rows">
            {board.entries.map((entry) => {
              const me = entry.playerId === playerId;
              const podium = entry.rank <= 3 ? ` lb__row--${entry.rank}` : '';
              return (
                <button
                  key={entry.playerId}
                  type="button"
                  className={`lb__row${podium}${me ? ' lb__row--me' : ''}`}
                  title="Open this run's board"
                  onClick={() =>
                    setPeek({
                      playerId: entry.playerId,
                      displayName: entry.displayName,
                      score: entry.score,
                      wavesCleared: entry.wavesCleared,
                      coreHp: entry.coreHp,
                      rank: entry.rank,
                      isMe: me,
                    })
                  }
                >
                  <span className="lb__rank">#{entry.rank}</span>
                  <span className="lb__player">
                    <span className="lb__name">{entry.displayName || 'anonymous'}</span>
                    {me && <span className="lb__tag">YOU</span>}
                  </span>
                  <span className="lb__num">{entry.wavesCleared}</span>
                  <span className="lb__num">{entry.coreHp}</span>
                  <span className="lb__score">{entry.score.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="lb__foot">
          <span className="lb__count">
            {runsToday} run{runsToday === 1 ? '' : 's'}
            {ownRank !== null ? ` · you are #${ownRank}` : ''}
          </span>

          <div className="lb__nav">
            <button
              type="button"
              className="lb__chip"
              disabled={previous === null}
              title={previous ? `Board for ${previous}` : 'No earlier board'}
              onClick={() => previous && setViewDate(previous)}
            >
              <span>&lsaquo; EARLIER</span>
            </button>
            <div className="lb__day">
              <span className="lb__day-date">{viewDate}</span>
              <span className="lb__day-note">{dateLabel(viewDate, date).toLowerCase()}</span>
            </div>
            <button
              type="button"
              className="lb__chip"
              disabled={next === null}
              title={next ? `Board for ${next}` : 'Already on the latest board'}
              onClick={() => next && setViewDate(next)}
            >
              <span>LATER &rsaquo;</span>
            </button>
          </div>

          <label className="lb__name-field">
            <span>YOUR NAME</span>
            <input
              type="text"
              maxLength={24}
              placeholder="anonymous"
              value={displayName}
              onChange={(e) => onDisplayName(e.target.value)}
              title="Applies from your next run"
            />
          </label>
        </div>
      </div>

      {peek && <RunPeek date={viewDate} target={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}
